/**
 * The client-side mirror of the server's `progressEngine.js`.
 *
 * Percentages are computed on the server and should be rendered as given. But a
 * few screens derive a percentage locally from rows they already hold, and when
 * they each spelled the formula out inline they drifted apart — one page divided
 * by the target twice (cancelling it out), another read only the newest
 * submission instead of the approved total, a third averaged completed-task
 * counts and called it project progress. This module is the one definition, so
 * those screens agree with the API and with each other.
 *
 * ## The rules, identical to the server
 *
 * - taskPercent    = clamp(0..100, approvedScoreSum / targetValue * 100)
 * - projectPercent = SUM(taskPercent × task.weight) / SUM(task.weight)
 * - expected       = clamp(0..100, elapsed / duration * 100)
 *
 * `targetValue` is the score at which a task counts as done: a task with target
 * 50 holding 50 approved points is 100% complete, not 50%. Anything at or above
 * its target reads as exactly 100 — the clamp is what guarantees a score beyond
 * the target never renders as 120%.
 *
 * Expected is pure calendar pace. It says where the work *should* be by today
 * given its dates, and never consults actual progress — an expected figure
 * derived from actual can only ever agree with it, which would make "behind
 * schedule" unreachable.
 */

/** Weight assumed for a task whose own weight is missing or <= 0. */
export const DEFAULT_WEIGHT = 50;

/** Every percentage this app displays is bounded to 0..100. */
export function clampPercent(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
}

const weightOf = (entity) => {
    const w = Number(entity?.weight);
    return Number.isFinite(w) && w > 0 ? w : DEFAULT_WEIGHT;
};

/**
 * Approved AND reviewed submission scores, summed.
 *
 * Tolerates the several shapes the API layers hand back: raw `inProgressEntries`
 * with `status: true`, and mapped `submissions` with `status: 'approved'` and
 * the score under `progressPercent`.
 */
export function approvedScoreSum(task) {
    const rows = Array.isArray(task?.inProgressEntries)
        ? task.inProgressEntries
        : Array.isArray(task?.submissions)
          ? task.submissions
          : [];

    const sum = rows
        .filter(
            (r) =>
                r?.reviewed === true &&
                (r?.status === true || r?.status === 'approved'),
        )
        .reduce(
            (acc, r) => acc + (Number(r?.score ?? r?.progressPercent) || 0),
            0,
        );
    // Decimal scores (32.23) drift when summed in binary floating point.
    return Math.round(sum * 100) / 100;
}

/**
 * A task's completion as a percent OF ITS TARGET.
 *
 * Pass `sum` when the caller already has the approved total (several API
 * mappers precompute `approvedScoreSum` on the row).
 */
export function taskPercent(task, sum = null) {
    const target = Number(task?.targetValue) || 100;
    if (target <= 0) return 0;
    const approved =
        sum != null
            ? Number(sum) || 0
            : Number(task?.approvedScoreSum) >= 0
              ? Number(task.approvedScoreSum)
              : approvedScoreSum(task);
    return clampPercent((approved / target) * 100);
}

/**
 * Project completion: the task-weight-weighted mean of its tasks' percents.
 *
 * NOT the share of tasks marked complete — that answers a different question and
 * ignores both weight and partial progress.
 */
export function projectPercentFromTasks(tasks) {
    let weighted = 0;
    let total = 0;
    for (const task of tasks ?? []) {
        const w = weightOf(task);
        weighted += taskPercent(task) * w;
        total += w;
    }
    return total > 0 ? clampPercent(weighted / total) : 0;
}

/**
 * Schedule pace: how far along this ought to be today, from its dates alone.
 * Returns 0 when there is no usable range rather than inventing a figure.
 */
export function expectedPercentFromDates(startRaw, endRaw, now = new Date()) {
    const start = startRaw ? new Date(startRaw) : null;
    const end = endRaw ? new Date(endRaw) : null;
    if (!start || !end) return 0;
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

    const durationMs = end.getTime() - start.getTime();
    if (!Number.isFinite(durationMs) || durationMs <= 0) return 0;

    const elapsedMs = now.getTime() - start.getTime();
    if (elapsedMs <= 0) return 0;

    return clampPercent((elapsedMs / durationMs) * 100);
}

/**
 * Schedule pace on the scale the task's own score is measured against.
 *
 * `expectedPercentFromDates` answers "how far through the schedule are we",
 * which is target-free. This answers "how much of this task's target should be
 * done by now" — and a task's actual percent is `approvedScore / targetValue *
 * 100`, so anything compared against it has to be normalized the same way.
 * Mirrors `expectedTaskPercentOfTarget` in the backend's `progressEngine.js`.
 */
export function expectedPercentOfTarget(startRaw, endRaw, targetValue, now = new Date()) {
    const elapsedPercent = expectedPercentFromDates(startRaw, endRaw, now);
    const target = Number(targetValue);
    // A missing or nonsensical target means "no normalization" rather than a
    // division by zero.
    if (!Number.isFinite(target) || target <= 0) return elapsedPercent;
    if (elapsedPercent >= target) return 100;
    return clampPercent((elapsedPercent * 100) / target);
}

/**
 * Round UP to one decimal place — 3.51 and 3.54 both read 3.6.
 *
 * Mirrors `ceilTo1` in the backend's `progressEngine.js`, epsilon included:
 * `8.2 * 10` is 82.00000000000001 in binary floating point, and a naive
 * `Math.ceil` would promote a clean 8.2 to 8.3.
 */
export function ceilTo1(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    // `|| 0` normalizes the negative zero `Math.ceil(0 - 1e-9)` produces.
    return Math.ceil(n * 10 - 1e-9) / 10 || 0;
}

/**
 * A percentage as it is displayed: one decimal, always up, trailing zero kept.
 *
 * The trailing zero is deliberate — "62.0" beside "47.6" keeps a column of
 * figures aligned on the decimal point and makes clear the number is precise to
 * a tenth rather than rounded to a whole.
 *
 * @returns {string} an em dash when there is no number to show, so a missing
 *   figure never renders as a confident "0.0".
 */
export function formatPercent1(value) {
    const rounded = ceilTo1(value);
    return rounded == null ? '—' : rounded.toFixed(1);
}

/** Actual minus expected, in points. Negative means behind schedule. */
export function variance(actual, expected) {
    if (actual == null || expected == null) return null;
    if (!Number.isFinite(Number(actual)) || !Number.isFinite(Number(expected))) {
        return null;
    }
    return Math.round((Number(actual) - Number(expected)) * 10) / 10;
}

export default {
    DEFAULT_WEIGHT,
    clampPercent,
    expectedPercentOfTarget,
    ceilTo1,
    formatPercent1,
    approvedScoreSum,
    taskPercent,
    projectPercentFromTasks,
    expectedPercentFromDates,
    variance,
};
